export interface MachineOutputOptions<TStructure> {
  text: string;
  structure: TStructure;
  onText: (visibleText: string) => void;
  onStructure: (structure: TStructure) => void;
  onComplete?: () => void;
  reducedMotion?: boolean;
}

export type MachineOutputResult = 'completed' | 'cancelled';

type SegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity: 'grapheme' },
) => {
  segment: (input: string) => Iterable<{ segment: string }>;
};

/**
 * 把已经完整返回的回答模拟成分批到达的文本流。
 * 该类只负责输出节奏和生命周期，不依赖 Vue，也不处理 Markdown 或业务卡片。
 */
export class MachineOutput<TStructure> {
  private readonly options: MachineOutputOptions<TStructure>;
  private readonly segments: string[];
  private cursor = 0;
  private visibleText = '';
  private timer: number | null = null;
  private status: 'idle' | 'running' | MachineOutputResult = 'idle';
  private settle: ((result: MachineOutputResult) => void) | null = null;

  constructor(options: MachineOutputOptions<TStructure>) {
    this.options = options;
    this.segments = MachineOutput.segmentText(options.text);
  }

  /** 开始输出。同一个实例只能启动一次。 */
  start(): Promise<MachineOutputResult> {
    if (this.status !== 'idle') {
      return Promise.resolve(this.status === 'completed' ? 'completed' : 'cancelled');
    }

    this.status = 'running';
    return new Promise((resolve) => {
      this.settle = resolve;

      if (this.shouldSkipAnimation() || this.segments.length === 0) {
        this.completeImmediately();
        return;
      }

      this.schedule(80);
    });
  }

  /** 取消尚未完成的输出，取消后不会再挂载结构数据。 */
  cancel(): void {
    if (this.status !== 'running') return;
    this.clearTimer();
    this.status = 'cancelled';
    this.settle?.('cancelled');
    this.settle = null;
  }

  /** 跳过剩余动画，但仍完整输出文本和结构数据。 */
  finish(): void {
    if (this.status !== 'running') return;
    this.clearTimer();
    this.completeImmediately();
  }

  private schedule(delay: number): void {
    this.timer = window.setTimeout(() => this.emitNextChunk(), delay);
  }

  private emitNextChunk(): void {
    if (this.status !== 'running') return;

    const chunkSize = this.getChunkSize();
    const nextCursor = Math.min(this.cursor + chunkSize, this.segments.length);
    const chunk = this.segments.slice(this.cursor, nextCursor).join('');
    this.cursor = nextCursor;
    this.visibleText += chunk;
    this.options.onText(this.visibleText);

    if (this.cursor >= this.segments.length) {
      this.complete();
      return;
    }

    this.schedule(this.getNextDelay(chunk));
  }

  /** 长回答自动增大每批字符数，避免输出耗时随文本长度无限增长。 */
  private getChunkSize(): number {
    const remaining = this.segments.length - this.cursor;
    const [min, max] = this.segments.length > 1200
      ? [6, 12]
      : this.segments.length > 600
        ? [3, 8]
        : [1, 4];
    return Math.min(remaining, MachineOutput.randomInteger(min, max));
    // return Math.min(remaining, MachineOutput.randomInteger(9999999, 99999999));
  }

  /** 标点和换行后略微停顿，让输出节奏更接近自然语言。 */
  private getNextDelay(chunk: string): number {
    if (/\n\s*$/.test(chunk)) return MachineOutput.randomInteger(90, 150);
    if (/[。！？!?]\s*$/.test(chunk)) return MachineOutput.randomInteger(100, 180);
    if (/[，、；：,;:]\s*$/.test(chunk)) return MachineOutput.randomInteger(55, 100);
    return MachineOutput.randomInteger(22, 48);
  }

  private completeImmediately(): void {
    this.visibleText = this.options.text;
    this.cursor = this.segments.length;
    this.options.onText(this.visibleText);
    this.complete();
  }

  /** 文本完成后才发送结构数据，确保卡片不会先于正文出现。 */
  private complete(): void {
    if (this.status !== 'running') return;
    this.clearTimer();
    this.options.onStructure(this.options.structure);
    this.options.onComplete?.();
    this.status = 'completed';
    this.settle?.('completed');
    this.settle = null;
  }

  private shouldSkipAnimation(): boolean {
    if (this.options.reducedMotion !== undefined) return this.options.reducedMotion;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  /** 优先按视觉字符切分，避免把 Emoji 或组合字符从中间拆开。 */
  private static segmentText(text: string): string[] {
    const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter;
    if (!Segmenter) return Array.from(text);
    return Array.from(new Segmenter(undefined, { granularity: 'grapheme' }).segment(text), item => item.segment);
  }

  private static randomInteger(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
